import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

import { ProgressBar, StepIndicator } from '../components/ProgressBar';
import { HeroSection } from '../components/HeroSection';
import { LeadCapture } from '../components/LeadCapture';
import { QuestionStep } from '../components/QuestionSteps';
import { calculateValuation } from '../lib/calculator';
import { SUBCATEGORIES, REVENUE_BASED_SUBCATEGORIES } from '../lib/constants';
import { notifyParentScrollTop, goToThankYou } from '../lib/embedBridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants for step transitions
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0
  })
};

// Define the step flow (without results - that's on thank you page)
const STEP_FLOW = [
  'hero',
  'leadCapture',
  'industry',
  'subcategory', // conditional
  'revenueTrend',
  'annualRevenue', // conditional (Finance only)
  'ownerEarnings',
  'plantEquipment',
  'state',
  'hasLease',
  'leaseLength', // conditional
  'phone',
  'sellingIntent'
];

// Industries that have subcategories
const INDUSTRIES_WITH_SUBCATEGORIES = Object.keys(SUBCATEGORIES);

function BusinessPricing() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [abandonmentReported, setAbandonmentReported] = useState(false);

  useEffect(() => { notifyParentScrollTop(); }, [currentStep]);
  
  const abandonmentTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  // Get current step key
  const getCurrentStepKey = () => STEP_FLOW[currentStep];
  
  // Check if a step should be shown (for conditional steps)
  const shouldShowStep = useCallback((stepKey, data = formData) => {
    if (stepKey === 'subcategory') {
      const hasSubcategories = INDUSTRIES_WITH_SUBCATEGORIES.includes(data.industry);
      return hasSubcategories;
    }
    if (stepKey === 'leaseLength') {
      return data.hasLease === 'yes';
    }
    if (stepKey === 'annualRevenue') {
      return data.industry === 'Finance' || REVENUE_BASED_SUBCATEGORIES.includes(data.subcategory);
    }
    return true;
  }, [formData]);
  
  // Get the actual step number for progress (excluding skipped steps)
  const getProgressInfo = () => {
    let visibleSteps = STEP_FLOW.filter((step) => step !== 'hero' && shouldShowStep(step, formData));
    
    const currentStepKey = getCurrentStepKey();
    const currentVisibleIndex = visibleSteps.indexOf(currentStepKey);
    
    return {
      current: Math.max(0, currentVisibleIndex + 1),
      total: visibleSteps.length
    };
  };
  
  // Update form data
  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    lastActivityRef.current = Date.now();
  };
  
  // Submit form and redirect to thank you page
  const submitAndRedirect = async (calculatedResults, data) => {
    setIsSubmitting(true);
    
    try {
      const submissionData = {
        session_id: sessionId,
        full_name: data.fullName,
        email: data.email,
        phone: data.phone || null,
        industry: data.industry,
        subcategory: data.subcategory || null,
        revenue_trend: data.revenueTrend,
        annual_revenue: parseFloat(data.annualRevenue) || 0,
        owner_earnings: parseFloat(data.ownerEarnings) || 0,
        plant_equipment_value: parseFloat(data.plantEquipment) || 0,
        state: data.state,
        has_lease: data.hasLease === 'yes',
        lease_length: data.leaseLength || null,
        selling_intent: data.sellingIntent,
        valuation_low: calculatedResults.valuationLow,
        valuation_high: calculatedResults.valuationHigh,
        base_multiple_low: calculatedResults.baseMultipleLow,
        base_multiple_high: calculatedResults.baseMultipleHigh,
        adjustments_applied: calculatedResults.adjustmentsApplied,
        calculation_method: calculatedResults.calculationMethod,
        alternative_values: calculatedResults.alternativeValues,
        buyer_demand_category: calculatedResults.buyerDemandCategory,
        business_strengths: calculatedResults.businessStrengths
      };
      
      // Submit to API
      const response = await axios.post(`${API}/submissions`, submissionData);
      
      if (response.data.status === 'success') {
        // Generate unique Business Enquiry ID for CRM/tracking
        const businessEnquiryId = 'BIZ-' + Date.now();
        sessionStorage.setItem('formID', businessEnquiryId);
        
        // Fire GTM/dataLayer conversion events
        try {
          if (typeof window !== 'undefined' && window.dataLayer) {
            // Push generate_lead event to dataLayer
            window.dataLayer.push({
              'event': 'generate_lead',
              'event_category': 'conversion',
              'event_label': 'business_valuation_completed',
              'value': calculatedResults.valuationHigh,
              'currency': 'AUD',
              'industry': data.industry,
              'selling_intent': data.sellingIntent,
              'business_enquiry_id': businessEnquiryId
            });
            
            // Push form_submission event to dataLayer
            window.dataLayer.push({
              'event': 'form_submission',
              'event_category': 'conversion',
              'event_label': 'valuation_completed',
              'valuation_low': calculatedResults.valuationLow,
              'valuation_high': calculatedResults.valuationHigh,
              'industry': data.industry,
              'subcategory': data.subcategory || '',
              'state': data.state,
              'selling_intent': data.sellingIntent,
              'business_enquiry_id': businessEnquiryId
            });
            
            console.log('GTM dataLayer events pushed successfully');
          } else {
            console.warn('GTM dataLayer not available');
          }
        } catch (gtmError) {
          console.error('GTM tracking error:', gtmError);
        }
        
        // Store results in sessionStorage for the thank you page
        const resultsData = {
          formData: data,
          results: calculatedResults,
          submissionId: response.data.id,
          businessEnquiryId: businessEnquiryId
        };
        sessionStorage.setItem('bonza_valuation_results', JSON.stringify(resultsData));
        
        // Full page redirect to thank you page (not SPA navigation)
        // This ensures GTM "Container Loaded" fires properly
        goToThankYou();
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error('There was an issue submitting your valuation. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Navigate to next step
  const goToNextStep = useCallback((updatedData = null) => {
    // Use the provided updated data or current formData
    const dataToUse = updatedData ? { ...formData, ...updatedData } : formData;
    
    let nextStep = currentStep + 1;
    
    // Skip conditional steps that shouldn't show - use the latest data
    while (nextStep < STEP_FLOW.length && !shouldShowStep(STEP_FLOW[nextStep], dataToUse)) {
      nextStep++;
    }
    
    // Check if we've completed all steps (past sellingIntent)
    if (nextStep >= STEP_FLOW.length) {
      // Calculate results and submit
      const calculatedResults = calculateValuation({
        industry: dataToUse.industry,
        subcategory: dataToUse.subcategory,
        revenueTrend: dataToUse.revenueTrend,
        annualRevenue: parseFloat(dataToUse.annualRevenue) || 0,
        ownerEarnings: parseFloat(dataToUse.ownerEarnings) || 0,
        plantEquipmentValue: parseFloat(dataToUse.plantEquipment) || 0,
        hasLease: dataToUse.hasLease === 'yes',
        leaseLength: dataToUse.leaseLength
      });
      
      // Submit and redirect
      submitAndRedirect(calculatedResults, dataToUse);
      return;
    }
    
    setDirection(1);
    setCurrentStep(nextStep);
  }, [currentStep, formData, shouldShowStep, navigate]);
  
  // Navigate to previous step
  const goToPrevStep = useCallback(() => {
    let prevStep = currentStep - 1;
    
    // Skip conditional steps that shouldn't show
    while (prevStep >= 0 && !shouldShowStep(STEP_FLOW[prevStep], formData)) {
      prevStep--;
    }
    
    setDirection(-1);
    setCurrentStep(Math.max(0, prevStep));
  }, [currentStep, formData, shouldShowStep]);
  
  // Report abandoned lead
  const reportAbandonedLead = useCallback(async () => {
    if (abandonmentReported || !formData.fullName || !formData.email) return;
    if (currentStep === 0 || currentStep === 1) return;
    
    try {
      await axios.post(`${API}/abandoned`, {
        full_name: formData.fullName,
        email: formData.email,
        session_id: sessionId,
        last_step: currentStep
      });
      setAbandonmentReported(true);
      console.log('Abandoned lead reported');
    } catch (error) {
      console.error('Failed to report abandoned lead:', error);
    }
  }, [abandonmentReported, formData, sessionId, currentStep]);
  
  // 5-minute abandonment tracking
  useEffect(() => {
    if (currentStep <= 1) {
      // Clear timer if on hero or lead capture
      if (abandonmentTimerRef.current) {
        clearTimeout(abandonmentTimerRef.current);
      }
      return;
    }
    
    const checkAbandonment = () => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity >= 5 * 60 * 1000) { // 5 minutes
        reportAbandonedLead();
      } else {
        // Schedule next check
        abandonmentTimerRef.current = setTimeout(
          checkAbandonment, 
          5 * 60 * 1000 - timeSinceActivity + 1000
        );
      }
    };
    
    // Start timer
    abandonmentTimerRef.current = setTimeout(checkAbandonment, 5 * 60 * 1000);
    
    return () => {
      if (abandonmentTimerRef.current) {
        clearTimeout(abandonmentTimerRef.current);
      }
    };
  }, [currentStep, reportAbandonedLead]);
  
  const stepKey = getCurrentStepKey();
  const progressInfo = getProgressInfo();
  const showProgress = currentStep > 0;
  
  return (
    <div className="bonza-container">
      <div className="bonza-card">
        {/* Progress Bar */}
        {showProgress && (
          <ProgressBar 
            currentStep={progressInfo.current} 
            totalSteps={progressInfo.total} 
          />
        )}
        
        {/* Step Indicator */}
        {showProgress && (
          <div className="px-6 pt-4 flex justify-end">
            <StepIndicator 
              currentStep={progressInfo.current} 
              totalSteps={progressInfo.total} 
            />
          </div>
        )}
        
        {/* Content */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {/* Hero */}
            {stepKey === 'hero' && (
              <HeroSection onStart={goToNextStep} />
            )}
            
            {/* Lead Capture */}
            {stepKey === 'leadCapture' && (
              <LeadCapture
                data={formData}
                onUpdate={updateFormData}
                onNext={goToNextStep}
              />
            )}
            
            {/* Question Steps */}
            {!['hero', 'leadCapture'].includes(stepKey) && (
              <QuestionStep
                stepKey={stepKey}
                data={formData[stepKey]}
                formData={formData}
                onUpdate={updateFormData}
                onNext={goToNextStep}
                onBack={goToPrevStep}
                canGoBack={currentStep > 2}
                isSubmitting={isSubmitting}
              />
            )}
          </motion.div>
        </AnimatePresence>
        
        {/* Submitting overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-[#5a2f92] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#5a2f92] font-medium">Calculating your valuation...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BusinessPricing;
